import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { Layout } from './components/layout/Layout';
import { queryClient } from './lib/api';
import { useAuthStore } from './lib/store';
import { Toaster } from './components/ui/toaster';

// Pages
import Dashboard from './pages/Dashboard';
import LoginPage from './pages/Login';

// Client Pages
import ClientsList from './pages/clients/ClientsList';
import ClientDetail from './pages/clients/ClientDetail';
import ClientForm from './pages/clients/ClientForm';

// Project Pages
import ProjectsList from './pages/projects/ProjectsList';
import ProjectDetail from './pages/projects/ProjectDetail';

// Contract Pages
import ContractsList from './pages/contracts/ContractsList';
import ContractDetail from './pages/contracts/ContractDetail';

// Team Pages
import TeamList from './pages/team/TeamList';
import TeamDetail from './pages/team/TeamDetail';
import TeamForm from './pages/team/TeamForm';

// Task Pages
import TasksKanban from './pages/tasks/TasksKanban';

// Time Tracking Pages
import TimeTracker from './pages/time/TimeTracker';
import TimeReports from './pages/time/TimeReports';

// Settings Pages
import IntegrationsSettings from './pages/settings/IntegrationsSettings';

// Protected Route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<LoginPage />} />

          {/* Protected Routes */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />

            {/* Client Routes */}
            <Route path="clients" element={<ClientsList />} />
            <Route path="clients/new" element={<ClientForm />} />
            <Route path="clients/:id" element={<ClientDetail />} />
            <Route path="clients/:id/edit" element={<ClientForm />} />

            {/* Project Routes */}
            <Route path="projects" element={<ProjectsList />} />
            <Route path="projects/:id" element={<ProjectDetail />} />

            {/* Contract Routes */}
            <Route path="contracts" element={<ContractsList />} />
            <Route path="contracts/:id" element={<ContractDetail />} />

            {/* Team Routes */}
            <Route path="team" element={<TeamList />} />
            <Route path="team/new" element={<TeamForm />} />
            <Route path="team/:id" element={<TeamDetail />} />
            <Route path="team/:id/edit" element={<TeamForm />} />

            {/* Task Routes */}
            <Route path="tasks" element={<TasksKanban />} />

            {/* Time Tracking Routes */}
            <Route path="time" element={<TimeTracker />} />
            <Route path="time/reports" element={<TimeReports />} />

            {/* Settings Routes */}
            <Route path="settings" element={<IntegrationsSettings />} />
          </Route>

          {/* Catch-all redirect */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster />
    </QueryClientProvider>
  );
}

export default App;
